/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { formatMoneyCompact } from '../../shared/format/moneyFormat';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet';
import type { ExpenseListSummary, ExpenseSettlementsSummary } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy';

type Props = {
  summary: ExpenseListSummary;
  paymentsSummary?: ExpenseSettlementsSummary | null;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  canEdit: boolean;
  canExport: boolean;
  onNewExpense: () => void;
  onExportCsv: () => void;
  activeCashSessionId: string | null;
  onOpenCash: () => void;
  hideSearch?: boolean;
  hideSummary?: boolean;
  paymentsMode?: boolean;
  onExportPaymentsCsv?: () => void;
  exporting?: boolean;
  kpiLoading?: boolean;
  kpiError?: string | null;
  /** Etiqueta del período activo — se muestra como scope del primer KPI. */
  kpiPeriodLabel?: string;
  /** La KPI "Vencidas" alterna el filtro de vencidas (si se pasa handler). */
  onOverdueKpiPress?: () => void;
  overdueKpiSelected?: boolean;
  /** PC: botón Filtros desk en la toolbar. */
  filterTrigger?: React.ReactNode;
};

type HeaderSubProps = Props & { searchInputRef: React.RefObject<TextInput | null> };

export function ExpensesScreenHeader(props: Props) {
  const { layoutWide } = useOperationalFilterLayout();
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: true,
  });

  const subProps: HeaderSubProps = { ...props, searchInputRef };

  if (layoutWide) return <WideHeader {...subProps} />;
  return <MobileHeader {...subProps} />;
}

function useExpenseKpiItems(
  summary: ExpenseListSummary,
  paymentsSummary: ExpenseSettlementsSummary | null | undefined,
  paymentsMode: boolean,
  layout: 'desk' | 'mobile' = 'desk',
  kpiLoading = false,
  kpiError: string | null = null,
  kpiPeriodLabel?: string,
  onOverdueKpiPress?: () => void,
  overdueKpiSelected?: boolean
): TicketKpiItem[] {
  return useMemo(() => {
    const dash = kpiLoading ? '—' : null;
    const errDash = kpiError ? '—' : null;
    const errTone = kpiError ? ('bad' as const) : undefined;
    const scope = kpiPeriodLabel ? ` · ${kpiPeriodLabel}` : '';
    if (paymentsMode) {
      // Proto: 3 tiles fijos — Pagado / Pagos / Salen de caja ($0 también informa).
      return [
        { label: `${expenseErpCopy.kpiPaidTotal}${scope}`, value: dash ?? errDash ?? formatMoneyCompact(paymentsSummary?.totalPaid ?? 0), tone: errTone },
        { label: expenseErpCopy.kpiPaymentCount, value: dash ?? errDash ?? String(paymentsSummary?.count ?? 0), tone: errTone },
        {
          label: expenseErpCopy.kpiCashOutflow,
          value: dash ?? errDash ?? formatMoneyCompact(paymentsSummary?.cashOutflowTotal ?? 0),
          tone: errTone,
        },
      ];
    }

    const money = (n: number) => dash ?? errDash ?? formatMoneyCompact(n);
    const count = (n: number) => dash ?? errDash ?? String(n);
    const moneyTone = (defaultTone?: 'warn' | 'bad') => errTone ?? defaultTone;
    const overdueKpi: TicketKpiItem = {
      label: expenseErpCopy.kpiOverdue,
      value: money(summary.overdueTotal),
      // Rojo solo si hay deuda vencida — $0 vencidas no es alarma.
      tone: moneyTone(summary.overdueTotal > 0 ? 'bad' : undefined),
      onPress: onOverdueKpiPress,
      selected: overdueKpiSelected,
    };

    if (layout === 'mobile') {
      // Proto mobile: fila fija de 3 — Total / Falta pagar / Vencidas.
      return [
        { label: `${expenseErpCopy.kpiBilledTotal}${scope}`, value: money(summary.total), tone: errTone },
        {
          label: expenseErpCopy.kpiBalanceDue,
          value: money(summary.unpaidTotal),
          tone: moneyTone(summary.unpaidTotal > 0 ? 'warn' : undefined),
        },
        overdueKpi,
      ];
    }

    // Proto desk: 4 tiles fijos — la cola Vencidas no desaparece en $0.
    return [
      { label: `${expenseErpCopy.kpiBilledTotal}${scope}`, value: money(summary.total), tone: errTone },
      {
        label: expenseErpCopy.kpiBalanceDue,
        value: money(summary.unpaidTotal),
        tone: moneyTone(summary.unpaidTotal > 0 ? 'warn' : undefined),
      },
      overdueKpi,
      { label: 'Facturas', value: count(summary.count), tone: errTone },
    ];
  }, [summary, paymentsSummary, paymentsMode, layout, kpiLoading, kpiError, kpiPeriodLabel, onOverdueKpiPress, overdueKpiSelected]);
}

function DeskActionTile({
  label,
  onPress,
  disabled,
  testID,
  accessibilityLabel,
  gold,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID: string;
  accessibilityLabel: string;
  gold?: boolean;
}) {
  return (
    <TouchableOpacity
      style={[gold ? styles.deskTileGold : styles.deskTile, disabled && posUi.disabledCard]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      testID={testID}
    >
      <Text style={gold ? styles.deskTileGoldText : styles.deskTileText}>{label}</Text>
    </TouchableOpacity>
  );
}

function WideHeader({
  summary,
  paymentsSummary,
  searchQuery,
  onSearchQueryChange,
  canEdit,
  canExport,
  onNewExpense,
  onExportCsv,
  activeCashSessionId,
  onOpenCash,
  hideSearch,
  hideSummary,
  paymentsMode,
  onExportPaymentsCsv,
  exporting = false,
  kpiLoading = false,
  kpiError = null,
  kpiPeriodLabel,
  onOverdueKpiPress,
  overdueKpiSelected,
  filterTrigger,
  searchInputRef,
}: HeaderSubProps) {
  const kpiItems = useExpenseKpiItems(summary, paymentsSummary, !!paymentsMode, 'desk', kpiLoading, kpiError, kpiPeriodLabel, onOverdueKpiPress, overdueKpiSelected);
  const moduleTitle = paymentsMode ? expenseErpCopy.tabPayments : expenseErpCopy.tabBills;
  const moduleMeta = paymentsMode
    ? 'Historial de pagos y rendiciones de gastos'
    : 'Facturas de proveedores y gastos operativos';

  return (
    <View style={{ gap: 10 }}>
      <TicketModuleHeader
        kicker="Gastos"
        title={moduleTitle}
        meta={moduleMeta}
        testID="gastos.header.sheet"
        trailing={
          canEdit ? (
            <DeskActionTile
              label={expenseErpCopy.actionNewBill}
              onPress={onNewExpense}
              disabled={!canEdit}
              testID="gastos.new"
              accessibilityLabel={expenseErpCopy.actionNewBill}
              gold
            />
          ) : null
        }
        tools={
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {!hideSearch ? (
              <View style={{ flex: 1, minWidth: 220 }}>
                <OperationalFilterSearchField
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  placeholder="Descripción, comprobante, proveedor o categoría"
                  appearance="desk"
                  accessibilityLabel="Buscar gastos"
                  testID="gastos.search"
                />
              </View>
            ) : null}
            {filterTrigger}
            {canExport && !paymentsMode ? (
              <DeskActionTile
                label="Exportar CSV"
                onPress={onExportCsv}
                disabled={exporting}
                testID="gastos.export-csv"
                accessibilityLabel="Descargar facturas filtradas en CSV"
              />
            ) : null}
            {paymentsMode && canExport && onExportPaymentsCsv ? (
              <DeskActionTile
                label="Exportar CSV"
                onPress={onExportPaymentsCsv}
                disabled={exporting}
                testID="gastos.export-payments-csv"
                accessibilityLabel="Descargar pagos filtrados en CSV"
              />
            ) : null}
          </View>
        }
      />

      {!hideSummary ? (
        <PaperSheet testID="gastos.kpi.sheet">
          <TicketKpiStrip items={kpiItems} layout="desk" variant="soft" />
        </PaperSheet>
      ) : null}

      {!paymentsMode && !activeCashSessionId ? (
        <TicketAlertStrip
          title="Sin turno de caja abierto"
          subtitle="Podés cargar facturas igual. Para sacar efectivo de la caja, abrí un turno."
          tone="warn"
          onPress={onOpenCash}
          accessibilityLabel="Ir a Caja"
          testID="gastos.cash-warn"
        />
      ) : null}
    </View>
  );
}

function MobileHeader({
  summary,
  paymentsSummary,
  searchQuery,
  onSearchQueryChange,
  canEdit,
  canExport,
  onNewExpense,
  onExportCsv,
  activeCashSessionId,
  onOpenCash,
  hideSearch,
  hideSummary,
  paymentsMode,
  onExportPaymentsCsv,
  exporting = false,
  kpiLoading = false,
  kpiError = null,
  kpiPeriodLabel,
  onOverdueKpiPress,
  overdueKpiSelected,
  filterTrigger,
  searchInputRef,
}: HeaderSubProps) {
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const kpiItems = useExpenseKpiItems(summary, paymentsSummary, !!paymentsMode, 'mobile', kpiLoading, kpiError, kpiPeriodLabel, onOverdueKpiPress, overdueKpiSelected);

  const sheetActions = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      hint: string;
      icon: React.ComponentProps<typeof Ionicons>['name'];
      onPress: () => void;
      disabled?: boolean;
    }> = [];
    if (canExport && !paymentsMode) {
      items.push({
        key: 'export-csv',
        label: exporting ? 'Exportando CSV…' : 'Exportar CSV',
        hint: 'Descargar facturas filtradas',
        icon: 'download-outline',
        onPress: onExportCsv,
        disabled: exporting,
      });
    }
    if (paymentsMode && canExport && onExportPaymentsCsv) {
      items.push({
        key: 'export-payments-csv',
        label: exporting ? 'Exportando CSV…' : 'Exportar CSV',
        hint: 'Descargar pagos filtrados',
        icon: 'download-outline',
        onPress: onExportPaymentsCsv,
        disabled: exporting,
      });
    }
    if (!paymentsMode && !activeCashSessionId) {
      items.push({
        key: 'open-cash',
        label: 'Ir a Caja',
        hint: 'Abrir o cerrar turno de caja',
        icon: 'cash-outline',
        onPress: onOpenCash,
      });
    }
    return items;
  }, [canExport, paymentsMode, onExportCsv, onExportPaymentsCsv, activeCashSessionId, onOpenCash, exporting]);

  const moduleTitle = paymentsMode ? expenseErpCopy.tabPayments : expenseErpCopy.tabBills;

  return (
    <View style={{ gap: 10 }}>
      <TicketModuleHeader
        kicker="Gastos"
        title={moduleTitle}
        testID="gastos.header.sheet"
        tools={
          <>
            {!hideSearch ? (
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search-outline" size={18} color={ticketColors.ink3} />
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSearchQueryChange}
                  placeholder="Buscar gastos"
                  placeholderTextColor={ticketColors.ink3}
                  style={styles.searchInputMobile}
                  accessibilityLabel="Buscar gastos"
                  autoCorrect={false}
                  returnKeyType="search"
                  blurOnSubmit={false}
                  testID="gastos.search"
                />
                {searchQuery.trim().length > 0 ? (
                  <TouchableOpacity
                    onPress={() => onSearchQueryChange('')}
                    accessibilityRole="button"
                    accessibilityLabel="Limpiar búsqueda"
                  >
                    <Ionicons name="close-circle" size={20} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
            <View style={styles.toolsRowMobile}>
              {filterTrigger}
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={styles.mobileMoreBtn}
                  onPress={() => setMoreActionsOpen(true)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Más opciones de gastos"
                  accessibilityHint={
                    sheetActions.length > 0
                      ? sheetActions.map((a) => a.label).join(', ')
                      : undefined
                  }
                >
                  <Text style={styles.mobileMoreBtnText}>Más</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {canEdit ? (
              <TouchableOpacity
                style={[styles.mobileNewBtn, !canEdit && posUi.disabledCard]}
                onPress={onNewExpense}
                disabled={!canEdit}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={expenseErpCopy.actionNewBill}
                accessibilityState={{ disabled: !canEdit }}
                testID="gastos.new"
              >
                <Text style={styles.mobileNewBtnText}>{expenseErpCopy.actionNewBill}</Text>
              </TouchableOpacity>
            ) : null}
          </>
        }
      >
        <TicketMoreActionsSheet
          visible={moreActionsOpen}
          title="Más opciones"
          actions={sheetActions}
          onClose={() => setMoreActionsOpen(false)}
        />
      </TicketModuleHeader>

      {!hideSummary ? (
        <PaperSheet testID="gastos.kpi.sheet">
          <TicketKpiStrip items={kpiItems} layout="mobile" variant="soft" />
        </PaperSheet>
      ) : null}

      {!paymentsMode && !activeCashSessionId ? (
        <TicketAlertStrip
          title="Sin turno de caja abierto"
          subtitle="Podés cargar facturas igual. Para sacar efectivo, abrí un turno."
          tone="warn"
          onPress={onOpenCash}
          accessibilityLabel="Ir a Caja"
          testID="gastos.cash-warn"
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  // ── Desk tiles (h42 / r10) ──
  deskTile: {
    height: 42,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  deskTileText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  deskTileGold: {
    height: 42,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  deskTileGoldText: {
    fontSize: 13,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
  // ── Mobile search ──
  searchBoxMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    paddingHorizontal: 12,
  },
  searchInputMobile: {
    flex: 1,
    fontSize: 15,
    color: ticketColors.ink,
    paddingVertical: 0,
  },
  // ── Mobile new button ──
  mobileNewBtn: {
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  mobileNewBtnText: {
    fontSize: 13,
    fontWeight: '800',
    color: ticketColors.goldInk,
  },
  toolsRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobileMoreBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileMoreBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
});
